import React, { memo } from 'react';

import { ClickableIcon } from '../ClickableIcon/ClickableIcon';
import { FloatingButton } from '../FloatingButton/FloatingButton';
import { IconClose, IconCloseSkinny } from '../../icons/colorless';
import { IconSize } from '../../icons/types';

export interface InfoBaseCloseButtonProps {
    /**
     * Обработчик клика на кнопку закрытия
     */
    onClose?: () => void;

    /**
     * Текст для title и aria-label кнопки
     */
    closeButtonText: string;

    /**
     * Размер иконки закрытия
     */
    iconSize: IconSize;

    /**
     * Вариант кнопки закрытия
     * - 'standard' - обычная кнопка с иконкой (для text, icon, illustration)
     * - 'floating' - плавающая кнопка для изображений и кастомного контента
     */
    variant?: 'standard' | 'floating';

    /**
     * Размер кликабельной области
     */
    clickableAreaSize?: string;

    /**
     * CSS класс для ClickableIcon
     */
    className?: string;

    /**
     * CSS класс для IconCloseSkinny
     */
    iconClassName?: string;

    /**
     * data-testid для тестирования
     */
    dataTestId?: string;
}

/**
 * Компонент кнопки закрытия для информационных компонентов.
 * Поддерживает различные варианты отображения в зависимости от типа контента.
 */
export const InfoBaseCloseButton = memo<InfoBaseCloseButtonProps>(function InfoBaseCloseButton({
    onClose,
    closeButtonText,
    iconSize,
    variant = 'standard',
    clickableAreaSize,
    className,
    iconClassName,
    dataTestId,
}) {
    if (!onClose) {
        return null;
    }

    if (variant === 'floating') {
        return (
            <FloatingButton
                icon={IconClose}
                size="2xs"
                color="contrast"
                className={className}
                onClick={onClose}
                title={closeButtonText}
                data-testid={dataTestId}
                aria-label={closeButtonText}
            />
        );
    }

    return (
        <ClickableIcon
            className={className}
            color="current"
            role="supplementary"
            clickableAreaSize={clickableAreaSize}
            onClick={onClose}
            title={closeButtonText}
            aria-label={closeButtonText}
            data-testid={dataTestId}
        >
            <IconCloseSkinny className={iconClassName} size={iconSize} />
        </ClickableIcon>
    );
});
